import { Container, Input } from 'components/atoms';
import StatisticsListContainer from 'components/molecules/statistics/StatisticsListContainer';
import { useCustomParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { PiChartBar, PiVinylRecord } from 'react-icons/pi';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  useGetLabelEarnings,
  useGetLabelTopProductsInfinite,
} from 'services/labels';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import { IStatisticsQuery } from 'services/statistics/types';
import { formatCurrency } from 'utils/global/currency.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';

const LabelStatistics = () => {
  usePermissionGuard('StatisticsPermissionsLIST**');

  const labelId = useCustomParamId('id', '/label');
  const settlementPeriod = useGetSettlementLightList();

  const [statisticsQuery, setStatisticsQuery] = useState<IStatisticsQuery>({
    startSettlementPeriodId: '',
    endSettlementPeriodId: '',
  });

  const [selectedProductId, setSelectedProductId] = useState<string>('');

  const topProducts = useGetLabelTopProductsInfinite({
    labelId,
    startSettlementPeriodId: statisticsQuery.startSettlementPeriodId,
    endSettlementPeriodId: statisticsQuery.endSettlementPeriodId,
    page: 0,
    itemCount: 10,
  });

  const earningsByProduct = useGetLabelEarnings(
    statisticsQuery.startSettlementPeriodId,
    statisticsQuery.endSettlementPeriodId,
    selectedProductId,
    !!selectedProductId &&
      !!statisticsQuery.startSettlementPeriodId &&
      !!statisticsQuery.endSettlementPeriodId
  );

  const options = useMemo(
    () =>
      settlementPeriod.data?.map(p => ({
        label:
          getSettlementPeriodName(p.settlementPeriodName) ||
          String(p.settlementPeriodName),
        value: p.id,
      })) ?? [],
    [settlementPeriod.data]
  );

  useEffect(() => {
    if (
      settlementPeriod.data &&
      !statisticsQuery.startSettlementPeriodId &&
      !statisticsQuery.endSettlementPeriodId
    ) {
      const end = settlementPeriod.data[0];
      const start = settlementPeriod.data[settlementPeriod.data.length - 1];

      setStatisticsQuery({
        endSettlementPeriodId: end.id,
        startSettlementPeriodId: start.id,
      });
    }
  }, [
    settlementPeriod.data,
    statisticsQuery.startSettlementPeriodId,
    statisticsQuery.endSettlementPeriodId,
  ]);

  useEffect(() => {
    if (
      topProducts.data?.pages?.[0] &&
      topProducts.data.pages[0].length > 0 &&
      !selectedProductId
    ) {
      setSelectedProductId(topProducts.data.pages[0][0].productId);
    }
  }, [topProducts.data, selectedProductId]);

  const mappedTopProducts = useMemo(() => {
    const allProducts = topProducts.data?.pages?.flatMap(page => page) ?? [];
    return allProducts
      .map(item => ({
        name: item.title,
        total: item.earnedAmount,
        productId: item.productId,
      }))
      .sort((a, b) => b.total - a.total);
  }, [topProducts.data]);

  const chartData = useMemo(() => {
    return (
      earningsByProduct.data?.earnings
        ?.map(earning => ({
          name: `${getSettlementPeriodName({
            name: earning.settlementPeriod.name,
            year: earning.settlementPeriod.year,
          })}`,
          value: earning.earnedAmount,
        }))
        .reverse() ?? []
    );
  }, [earningsByProduct.data]);

  return (
    <div className='flex gap-4 w-full flex-row-reverse'>
      <Container>
        <Container.Header
          title={
            <div className=' flex w-full justify-between'>
              <div className='flex gap-2'>
                {selectedProductId && topProducts.data?.pages?.[0] ? (
                  <>
                    <PiChartBar /> {t('statistics.earnings')} -{' '}
                    {topProducts.data.pages[0].find(
                      (p: any) => p.productId === selectedProductId
                    )?.title || ''}
                  </>
                ) : (
                  t('statistics.name')
                )}
              </div>
            </div>
          }>
          <div className='p-4 pointer-events-none opacity-0'>
            <Input.Select />
          </div>
        </Container.Header>
        <Container.Content>
          <ResponsiveContainer width={'100%'} height={370}>
            <LineChart
              data={chartData}
              margin={{
                top: 20,
                right: 100,
                bottom: 20,
              }}>
              <CartesianGrid
                strokeDasharray=''
                vertical={false}
                opacity={0.5}
              />
              <XAxis dataKey='name' stroke='var(--secondary-light)' />
              <YAxis
                width={150}
                stroke='var(--secondary-light)'
                tickFormatter={(value: any, index: number) =>
                  value === 0 ? '' : formatCurrency(parseFloat(`${value}`))
                }
                tickCount={5}
              />
              <Tooltip
                cursor={{ fill: 'none' }}
                content={props => (
                  <div
                    className='flex bg-opacity-95 rounded-sm p-8 gap-3'
                    style={{ backgroundColor: 'var(--background)' }}>
                    <p>{`${props.label}`}</p>
                    {props.payload && props.payload[0] && (
                      <>
                        <p>-</p>
                        <p style={{ color: props.payload[0].color }}>
                          {formatCurrency(
                            parseFloat(`${props.payload[0].value}`)
                          )}
                        </p>
                      </>
                    )}
                  </div>
                )}
              />
              <ReferenceLine y={0} stroke='var(--secondary-light)' />
              <Line
                type='monotone'
                dataKey='value'
                fill='var(--primary)'
                stroke='var(--primary)'
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Container.Content>
      </Container>

      <StatisticsListContainer
        classname='w-[40%] max-h-[441px] max-w-lg'
        headerTitle={
          <>
            <PiVinylRecord /> {t('statistics.top-products')}
          </>
        }
        label={item => item.name}
        value={item => formatCurrency(item.total)}
        items={mappedTopProducts}
        loading={topProducts.isLoading}
        onFullyScroll={topProducts.fetchNextPage}
        isHighlighted={item => {
          return item.productId === selectedProductId;
        }}
        onItemClick={item => {
          setSelectedProductId(item.productId);
        }}
        headerChildren={
          <div className='info-box-items flex p-4 w-full min-h-0 max-w-96'>
            <Input.ComplexWrapper className='w-full'>
              <Input.Select
                label={t('input.label.start')}
                value={
                  options.find(
                    opt => opt.value === statisticsQuery.startSettlementPeriodId
                  ) || undefined
                }
                onChange={option =>
                  setStatisticsQuery(prev => ({
                    ...prev,
                    startSettlementPeriodId: option?.value as string,
                  }))
                }
                placeholder=''
                options={options}
                isClearable={false}
              />
              <Input.Select
                label={t('input.label.end')}
                value={
                  options.find(
                    opt => opt.value === statisticsQuery.endSettlementPeriodId
                  ) || undefined
                }
                onChange={option =>
                  setStatisticsQuery(prev => ({
                    ...prev,
                    endSettlementPeriodId: option?.value as string,
                  }))
                }
                placeholder=''
                options={options}
                isClearable={false}
              />
            </Input.ComplexWrapper>
          </div>
        }
      />
    </div>
  );
};

export default LabelStatistics;
